import type { SortOrderDirection, NestedFilter } from '@@partner:types:graphql'
import type { GridSort } from '@@partner:types:app'
import type { SortType } from 'rsuite-table'

import { useState, useCallback } from 'react'
import { sortTypeToSortOrderDirection } from '../data-mappers/sort'

import { SORT_ORDER_DESC } from '@@partner:config/graphql'
import { convertToInt } from '@@partner:helpers/util'

export interface UseGridHelperArgs {
  defaultSortFieldName: string
  defaultSortDirection: SortOrderDirection
  defaultSortType: SortType
  defaultLimit: number
  defaultLimitOptions: number[]
  defaultFilter?: NestedFilter
}

const DEFAULT_LIMIT_OPTIONS = [ 10, 20, 50, 100 ]

const DEFAULT_USE_GRID_HELPER_ARGS: UseGridHelperArgs = {
  defaultSortFieldName: 'id',
  defaultSortDirection: SORT_ORDER_DESC,
  defaultSortType: 'desc',
  defaultLimit: 10,
  defaultLimitOptions: DEFAULT_LIMIT_OPTIONS,
  defaultFilter: {}
}

export const useGridHelper = ({
  defaultSortFieldName,
  defaultSortDirection,
  defaultSortType,
  defaultLimit,
  defaultLimitOptions,
  defaultFilter
}: UseGridHelperArgs = DEFAULT_USE_GRID_HELPER_ARGS) => {
  const [ page, setPage ] = useState(1)
  const [ limit, setLimit ] = useState(defaultLimit)
  const [ tableSort, setTableSort ] = useState<GridSort>({
    fieldName: defaultSortFieldName,
    direction: defaultSortDirection,
    sortType: defaultSortType
  })
  const [ filter, setFilter ] = useState<NestedFilter>(defaultFilter ?? {})

  const handlePageSelect = useCallback((selectedPage: number | string) => {
    setPage(convertToInt(selectedPage))
  }, [])

  const handleFilterChanged = useCallback(
    (updatedFilter: NestedFilter) => {
      setFilter(updatedFilter)
      handlePageSelect(1)
    },
    [ handlePageSelect ]
  )

  const handleLimitSelect = useCallback((selectedLimit: number | null) => {
    setLimit(selectedLimit ?? DEFAULT_USE_GRID_HELPER_ARGS.defaultLimit)
  }, [])

  const handleSortColumn = useCallback(
    (fieldName: string, sortType: SortType = defaultSortType) => {
      setTableSort({
        fieldName,
        sortType,
        direction: sortTypeToSortOrderDirection(sortType)
      })
    },
    [ defaultSortType ]
  )

  return {
    page,
    limit,
    defaultLimitOptions,
    filter,
    tableSort,
    handlePageSelect,
    handleLimitSelect,
    handleFilterChanged,
    handleSortColumn
  }
}
